import { createStore } from './createStore';
import { useProjectImagesStore } from './useProjectImagesStore';
import { normalizeAssetImageFields } from '../utils/assetImageResolver';

const initialState = {
  assets: [],
  assetAnimatingId: null,
};

function currentImagesById() {
  return useProjectImagesStore.getState().imagesById || {};
}

function areAssetListsEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;

  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (err) {
    return false;
  }
}

/**
 * Asset inventory store (characters, props, etc.).
 */
export const useAssetsStore = createStore('AssetsStore', (set) => ({
  ...initialState,

  setAssets: (assets) =>
    set((state) => {
      const nextAssets = Array.isArray(assets)
        ? assets.map((asset) => normalizeAssetImageFields(asset, currentImagesById()))
        : [];
      if (areAssetListsEqual(state.assets, nextAssets)) return state;
      return { assets: nextAssets };
    }),

  addAsset: (asset) =>
    set((state) => {
      if (!asset?.id) return state;
      const normalized = normalizeAssetImageFields(asset, currentImagesById());
      const existingIndex = state.assets.findIndex((existing) => existing.id === asset.id);
      if (existingIndex >= 0) {
        const assets = [...state.assets];
        const nonEmptyUpdates = Object.fromEntries(
          Object.entries(normalized).filter(([key, value]) => {
            if (value === undefined || value === null) return false;
            if ((key === 'imageUrl' || key === 'videoUrl' || key === 'audioUrl') && value === '') {
              return false;
            }
            return true;
          })
        );
        assets[existingIndex] = normalizeAssetImageFields(
          { ...assets[existingIndex], ...nonEmptyUpdates },
          currentImagesById()
        );
        return { assets };
      }
      return { assets: [normalized, ...state.assets] };
    }),

  updateAsset: (assetId, updates) =>
    set((state) => ({
      assets: state.assets.map((asset) => {
        if (asset.id !== assetId) return asset;
        const definedUpdates = Object.fromEntries(
          Object.entries(updates || {}).filter(([, value]) => value !== undefined)
        );
        return normalizeAssetImageFields({ ...asset, ...definedUpdates }, currentImagesById());
      }),
    })),

  deleteAsset: (assetId) =>
    set((state) => ({
      assets: state.assets.filter((asset) => asset.id !== assetId),
      assetAnimatingId: state.assetAnimatingId === assetId ? null : state.assetAnimatingId,
    })),

  setAssetAnimatingId: (assetId) => set({ assetAnimatingId: assetId }),

  resetAssets: () => set(() => initialState),
}));
